import { createRoot } from 'react-dom/client';
import { createApiClient } from '../api/client';
import { readRuntimeConfig } from '../config';
import { createStripeBridge } from '../payments/stripe';
import { createGiftcardController } from '../state/giftcard';
import { purchaseFromBooking, trackPurchase } from '../tracking/ga4';
import { GiftCardApp } from '../ui/giftcard/GiftCardApp';
import { GiftContext } from '../ui/giftcard/context';
import '../ui/styles.css';

async function boot(): Promise<void> {
  const mount = document.querySelector<HTMLElement>('[data-cbf-gift-root]');
  if (!mount) return;
  const config = readRuntimeConfig();

  let fetchImpl: typeof fetch | undefined;
  let stripeFactory = createStripeBridge;
  if (import.meta.env.DEV && config.mock) {
    // Dev-server only; the whole branch is tree-shaken from production builds. The Stripe
    // bridge is mocked too — real Stripe.js rejects the mock API's fake intent secrets.
    const [{ createMockTransport }, { createMockStripeBridge }] = await Promise.all([
      import('../api/mock/transport'),
      import('../payments/mockBridge'),
    ]);
    fetchImpl = createMockTransport({ baseUrl: config.apiBase, latencyMs: 150 }).fetch;
    stripeFactory = createMockStripeBridge;
  }

  try {
    const client = createApiClient({
      baseUrl: config.apiBase,
      env: config.env,
      allowProduction: config.allowProduction,
      fetch: fetchImpl,
    });
    const stripe = stripeFactory();
    const controller = createGiftcardController({
      client,
      config,
      createPaymentMethod: (billing) => stripe.createPaymentMethod(billing),
      handleCardAction: (secret) => stripe.handleCardAction(secret),
      onSuccess: (result, state) => {
        const currency = (state.settings?.currency?.code ?? 'usd').toUpperCase();
        const purchase = purchaseFromBooking(result, currency);
        if (purchase) void trackPurchase(purchase);
      },
    });
    mount.removeAttribute('aria-busy');
    createRoot(mount).render(
      <GiftContext.Provider value={{ controller, config, stripe }}>
        <GiftCardApp />
      </GiftContext.Provider>,
    );
    const state = await controller.start();
    if (state.status === 'ready') void stripe.load(state.settings?.stripe_public_key);
  } catch (err) {
    mount.textContent = 'Gift cards could not be loaded. Please refresh the page or contact us.';
    console.error('[cbf-gift]', err);
  }
}

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', () => void boot());
else void boot();
